import { useState, useEffect, useMemo } from 'react'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import './MarkdownStyles.css'
import { Link } from 'react-router-dom'
import matter from 'gray-matter'
import PostCard from '../components/PostCard'

const POSTS_PER_LOAD = 12

const slugFromFilePath = (path) => path.split('/')[1]

const BlogPage = () => {
    const [htmlContents, setHtmlContents] = useState([])
    const [allFiles, setAllFiles] = useState([])
    const [currentIndex, setCurrentIndex] = useState(0)
    const [loading, setLoading] = useState(false)
    const [initialLoad, setInitialLoad] = useState(true)
    const [error, setError] = useState(null)
    const [tagFilter, setTagFilter] = useState(null)
    const [expandedPosts, setExpandedPosts] = useState({})
    const [pinnedPaths, setPinnedPaths] = useState([])

    useEffect(() => {
        const updateTagsFromHash = () => {
            const hash = window.location.hash.replace('#', '').toLowerCase()
            const tags = hash ? hash.split('+').filter(Boolean) : []
            setTagFilter(tags.length ? tags : null)
        }

        updateTagsFromHash()
        window.addEventListener('hashchange', updateTagsFromHash)
        return () => window.removeEventListener('hashchange', updateTagsFromHash)
    }, [])

    useEffect(() => {
        const loadConfig = async () => {
            try {
                const [configRes, pinsRes] = await Promise.all([
                    fetch('/BlogContent/files.json'),
                    fetch('/BlogContent/pins.json')
                ])

                const config = await configRes.json()
                const pins = await pinsRes.json()

                const all = Array.from(new Set([...(pins.pinned || []), ...config.files]))

                setAllFiles(all)
                setPinnedPaths(pins.pinned || [])
            } catch (err) {
                setError(`Failed to load blog config: ${err.message}`)
            } finally {
                setInitialLoad(false)
            }
        }

        loadConfig()
    }, [])

    useEffect(() => {
        if (allFiles.length > 0 && htmlContents.length === 0) {
            loadMorePosts()
        }
    }, [allFiles])

    const loadMorePosts = async () => {
        if (loading || currentIndex >= allFiles.length) return
        setLoading(true)

        const filesToLoad = allFiles.slice(
            currentIndex,
            currentIndex + POSTS_PER_LOAD
        )

        const newPosts = []
        for (const filePath of filesToLoad) {
            try {
                const response = await fetch(`/${filePath}`)
                if (!response.ok) throw new Error(`File "${filePath}" not found`)

                const rawContent = await response.text()
                const isMarkdown = filePath.endsWith('.md')
                let content = rawContent

                if (isMarkdown) {
                    const { content: markdownContent, data: frontmatter } = matter(rawContent)
                    const [preview, rest] = markdownContent.split('<!--more-->')

                    const previewHTML = marked.parse(preview)
                    const fullHTML = rest ? marked.parse(rest) : ''

                    content = {
                        preview: previewHTML,
                        full: fullHTML,
                        isExpandable: !!rest,
                        meta: {
                            ...frontmatter,
                            tags: frontmatter.tags || [],
                        }
                    }
                } else {
                    content = { preview: rawContent, full: '', isExpandable: false }
                }

                newPosts.push({ path: filePath, content })

            } catch (err) {
                newPosts.push({
                    path: filePath,
                    content: {
                        preview: `<div class='error'>Error loading "${filePath}"</div>`,
                        full: '',
                        isExpandable: false,
                        meta: {
                            title: 'Error',
                            date: new Date().toISOString()
                        }
                    }
                })
            }
        }

        setHtmlContents(prev => [...prev, ...newPosts])
        setCurrentIndex(prev => prev + POSTS_PER_LOAD)
        setLoading(false)
    }

    const toggleExpand = (path) => {
        setExpandedPosts(prev => ({
            ...prev,
            [path]: !prev[path]
        }))
    }

    const filteredPosts = useMemo(() => {
        if (!tagFilter) return htmlContents

        return htmlContents.filter(({ content }) =>
            Array.isArray(content.meta?.tags) &&
            content.meta.tags.some(tag =>
                tagFilter.includes(tag.toLowerCase())
            )
        )
    }, [htmlContents, tagFilter])

    if (error) return <div className='error'>Error: {error}</div>

    const pinnedPosts = filteredPosts.filter(p => pinnedPaths.includes(p.path))
    const regularPosts = filteredPosts.filter(p => !pinnedPaths.includes(p.path))

    return (
        <div className='blog-page'>
            <div className='container'>
                {tagFilter && (
                    <div className="tag-filter-banner">
                        Showing posts tagged with{' '}
                        {tagFilter.map(tag => (
                            <strong key={tag}>#{tag} </strong>
                        ))}
                        <button onClick={() => setTagFilter(null)}>Clear filter</button>
                    </div>
                )}

                <div className='posts-wrapper'>
                    {[...pinnedPosts, ...regularPosts].map(({path, content}) => (
                        <PostCard
                            key={path}
                            path={path}
                            content={content}
                            isExpanded={expandedPosts[path]}
                            pinned={pinnedPaths.includes(path)}
                            onToggleExpand={toggleExpand}
                        />
                    ))}
                </div>

                {initialLoad && <div className='loader'>Loading blog...</div>}
                {loading && !initialLoad && <div className='loader'>Loading more posts...</div>}

                {!tagFilter && currentIndex < allFiles.length && (
                    <button className="load-more-button" onClick={loadMorePosts} disabled={loading}>
                        {loading ? 'Loading...' : 'Load More'}
                    </button>
                )}
            </div>
        </div>
    )
}

export default BlogPage
